import * as React from 'react';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Navigation } from '@toolpad/core/AppProvider';
import { Group } from '../model/group';
import { Course } from '../model/course';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reducers/store';
import { addCourseAsync, updateCourse } from '../reducers/courseSlice';
import { v4 as uuidv4 } from 'uuid';
import IconButton from '@mui/material/IconButton';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import { ArrowDropDown, ArrowDropUp, LibraryAdd, SupportOutlined } from '@mui/icons-material';
import { getApiAccessToken } from '../auth/auth';
import { reorderCourse } from '../api/courses';
import { useMsal } from '@azure/msal-react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { Program } from '../model/program';
import { addProgramAsync, getProgramsAsync } from '../reducers/programSlice';
import { getUserAccess, UserPermission, UserRole } from '../auth/access';
import Typography from '@mui/material/Typography';
import { supportInfo } from './support';
import Button from '@mui/material/Button';

interface MenuNavigationProps {
  accessToken: string | null;
  programs: Program[];
  courses: Course[];
}

interface AddModuleProps {
  accessToken: string | null;
  group: Group | null;
  program: Program | null;
  courses: Course[];
}

interface AddProgramProps {
  accessToken: string | null;
  group: Group | null
}


interface ReorderModuleProps {
  courseToReorder: Course;
  courses: Course[];
}

const AddModuleAction = ({ accessToken, group, program, courses }: AddModuleProps): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();

  if (group == null) {
    console.log("client not selected yet")
    return <></>
  }

  if (program == null) {
    console.log("program not selected yet")
    return <></>
  }

  if (accessToken == null) {
    console.log("unexpected access token is null")
    return <></>
  }

  const currentCourses = courses.filter(course => course.role === group?.displayName)

  const handleAddModuleClick = (course: Course) => {
    dispatch(addCourseAsync({accessToken, course}));

    // we refetch programs with the new course added
    dispatch(getProgramsAsync({accessToken}));
  };

  return (
    <IconButton onClick={(e) => {
      // prevent click from collapsing the menu
      e.stopPropagation(); 

      handleAddModuleClick({
        name: `New ${group.displayName} Module`,
        introduction: '',
        uuid: uuidv4().toString(),
        role: group.displayName,
        programId: program.id,
        sessions: [],
        index: currentCourses.length
      })}}>
      <AddIcon />
    </IconButton>
  );
};

const AddProgramAction = ({ accessToken, group }: AddProgramProps): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();

  if (group == null) {
    console.log("client not selected yet")
    return <></>
  }

  if (accessToken == null) {
    console.log("unexpected access token is null")
    return <></>
  }

  const handleAddProgramClick = (accessToken: string, program: Program) => {
    dispatch(addProgramAsync({accessToken, program}));
    
  };

  return (
    <IconButton onClick={(e) => {
      // prevent click from collapsing the menu
      e.stopPropagation(); 

      handleAddProgramClick(accessToken, {
        name: `New ${group.displayName} Program`,
        id: uuidv4().toString(),
        clientName: group.displayName,
        description: `A program made by ${group.displayName}`,
        courses: []
    })}}>
      <AddIcon />
    </IconButton>
  );
};

const reorderCoursesFromDirection = async (direction: number, dispatch: AppDispatch, instance: IPublicClientApplication, course: Course, courses: Course[]) => {
  let index = course.index + direction
  return reorderCoursesFromIndex(index, dispatch, instance, course, courses)
}
  
const reorderCoursesFromIndex = async (index: number, dispatch: AppDispatch, instance: IPublicClientApplication, course: Course, courses: Course[]) => {
  const activeAccount = instance.getActiveAccount();

  let reorderedCourse: Course = { ...course };

  if (!reorderedCourse?.index || reorderedCourse == undefined || reorderedCourse.index == null || Number.isNaN(reorderedCourse.index)) {
    reorderedCourse.index = 1
  }

  reorderedCourse.index = index

  dispatch(updateCourse(reorderedCourse))

  try {
    let accessToken: string = await getApiAccessToken(instance, activeAccount)
    await reorderCourse(accessToken, reorderedCourse.uuid, reorderedCourse.index);
  } catch (error) {
      console.error(error);
  }

  for(let existingCourse of courses) {
    //exchange the index with another course that currently has that index if it exists
    if (existingCourse.index == reorderedCourse.index && existingCourse.uuid != reorderedCourse.uuid) {

      let takeOldIndexCourse: Course = { ...existingCourse };
      takeOldIndexCourse.index = course.index

      dispatch(updateCourse(takeOldIndexCourse))

      try {
        let accessToken: string = await getApiAccessToken(instance, activeAccount)
        await reorderCourse(accessToken, takeOldIndexCourse.uuid, takeOldIndexCourse.index);
      } catch (error) {
          console.error(error);
      }

      break;
    }
  }
}

// Reordering can be tricky due to frequently changing size of list of items to order, as well as the possibility of deletion of content causing a gap in indices
// therefore we assume order can exceed list size and we set the index based on current list size at the upper bound and to 0 at the lower bound
const ReorderAction = ({courseToReorder, courses}: ReorderModuleProps): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const { instance } = useMsal();

  if (courseToReorder.index < 0) {
      return(
        <IconButton onClick={() => {reorderCoursesFromIndex(0, dispatch, instance, courseToReorder, courses)}}>
          <ArrowDropDown style={{fontSize: 'small'}}/>
        </IconButton>
      )
  } else if (courseToReorder.index == 0) {
      return(
        <IconButton onClick={() => {reorderCoursesFromDirection(1, dispatch, instance, courseToReorder, courses)}}>
          <ArrowDropDown style={{fontSize: 'small'}}/>
        </IconButton>
      )
  } else if (courseToReorder.index == courses.length - 1) {
      return(
        <IconButton onClick={() => {reorderCoursesFromDirection(-1, dispatch, instance, courseToReorder, courses)}}>
          <ArrowDropUp style={{fontSize: 'small'}}/>
        </IconButton>
      )
  } else if (courseToReorder.index > courses.length - 1) {
      return(
        <IconButton onClick={() => {reorderCoursesFromIndex(courses.length - 1, dispatch, instance, courseToReorder, courses)}}>
          <ArrowDropUp style={{fontSize: 'small'}}/>
        </IconButton>
      )
  } else {
    return (
      <React.Fragment>
        <IconButton onClick={() => {reorderCoursesFromDirection(-1, dispatch, instance, courseToReorder, courses)}}>
          <ArrowDropUp style={{fontSize: 'small'}}/>
        </IconButton>
        <IconButton onClick={() => {reorderCoursesFromDirection(1, dispatch, instance, courseToReorder, courses)}}>
          <ArrowDropDown style={{fontSize: 'small'}}/>
        </IconButton>
      </React.Fragment>
    );
  }
}

const truncate = (text: string, maxLen = 30): string =>
    text.length > maxLen ? text.substring(0, maxLen) + '\u2026' : text;

export const MenuNavigation = ( {accessToken, programs, courses}: MenuNavigationProps): Navigation => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const userAccess = getUserAccess(activeAccount);

    const currentProgram: Program | null = useSelector((state: RootState) => state.programs.currentProgram);
    const currentGroup: Group | null = useSelector((state: RootState) => state.groups.currentGroup);
    const currentCourses: Course[] = courses.filter(course => course.programId === currentProgram?.id)

    if (userAccess?.role == UserRole.ADMIN) {
        return [
            {
              segment: 'clients',
              title: 'Clients',
              icon: <AppRegistrationIcon/>
            },
            {
              kind: 'divider',
            },
            {
              title: 'Programs',
              segment: 'programs',
              icon: <LibraryAdd/>,
              action: <AddProgramAction accessToken={accessToken} group={currentGroup} />,
              children: programs.filter(program => program.clientName == currentGroup?.displayName).map((program: Program, index: number) => ({
                segment: program.id,
                title: truncate(program.name),
              }))
            },
            {
              title: 'Chapters',
              segment: 'chapters',
              icon: <SchoolIcon/>,
              action: <AddModuleAction accessToken={accessToken} group={currentGroup} program={currentProgram} courses={courses} />,
              children: courses?.length > 0 ? currentCourses.toSorted((a: Course, b: Course) => a.index - b.index).map((course: Course, index: number) => ({
                segment: course.uuid,
                title: truncate(course.name),
                action: <ReorderAction courseToReorder={course} courses={currentCourses} />,
              })) : []
            },
            {
              kind: 'divider',
            },
            {
              title: 'Support',
              segment: 'support',
              icon: <SupportOutlined/>,
            }
          ]

    } else if (userAccess?.role == UserRole.CLIENT_ADMIN && userAccess?.permissions.includes(UserPermission.MANAGE_CONTENT)) {
        return [
            {
              title: 'Programs',
              segment: 'programs',
              icon: <LibraryAdd/>,
              children: programs.filter(program => program.clientName == currentGroup?.displayName).map((program: Program, index: number) => ({
                segment: program.id,
                title: truncate(program.name),
              }))
            },
            {
              title: 'Chapters',
              segment: 'chapters',
              icon: <SchoolIcon/>,
              action: <AddModuleAction accessToken={accessToken} group={currentGroup} program={currentProgram} courses={courses} />,
              children: courses?.length > 0 ? currentCourses.toSorted((a: Course, b: Course) => a.index - b.index).map((course: Course, index: number) => ({
                segment: course.uuid,
                title: truncate(course.name),
                action: <ReorderAction courseToReorder={course} courses={currentCourses} />,
              })) : []
            }
          ]
    } else if (userAccess?.role == UserRole.CLIENT_ADMIN) {
        return [
          {
            title: 'Programs',
            segment: 'programs',
            icon: <LibraryAdd/>,
            children: programs.filter(program => program.clientName == currentGroup?.displayName).map((program: Program, index: number) => ({
              segment: program.id,
              title: truncate(program.name),
            }))
          }
        ];
    } else {
        return []
    }
}